
import Vue, { PropType } from 'vue';
import {
  Artwork,
  ArtworkResult,
  EnrichPositionWithArtworkForm,
  w2pFileUploader,
} from '@/components/w2pFileUploader';
import { PmsColor, OrderLineDetail } from '@midocean/webapps-shared-domain';
import { FullDesignUploader } from '../flow-starter/components/FullDesignUploader';
import { validateFn, customValidateFn } from '@/validators/basicFileValidator';
import FileService from '@/services/FileService';
import { supportedExtensions as supportedExtensionsForLogoOnly } from '@/constants/supportedExtensions';
import { AnnotationShape } from '../import-wizard/typings/pintura';
import {
  constructArtworkValidationPipeline,
  ValidatorResult,
} from '@/validators';
import { useStore as useProductStore } from '@/stores/product';
import { mapStores } from 'pinia';
import { mapActions, PiniaVuePlugin } from 'pinia';
import {
  FileUploadedPayload,
  PreparedValidationResult,
  SelectedPrintPositionData,
  ProductNotPrintableEvent,
  ValidatorNotification,
} from './typings/index';
import { CvInlineNotification } from '@carbon/vue';
import customersAPI from '@/services/CustomersService';
import { v4 as uuidv4 } from 'uuid';
import {
  ProductVariant as NewPersonalizationVariant,
  ProductPrintVariant,
  ProductPrintData,
} from '@midocean/webapps-shared-domain';
import { ProductPrintPositionBoxModel } from '@/typings/ProductPrintData';
import { SupportedLangCodes } from '@/constants/languageCodes';

Vue.use(PiniaVuePlugin);

const FILE_UPLOADER_HELPER_TEXT_LOGO_ONLY =
  'upload-logo.file-uploader-helper-text.logo-only';
const FILE_UPLOADER_HELPER_TEXT_FULL_DESIGN =
  'upload-logo.file-uploader-helper-text.full-design';

export default Vue.extend({
  components: { w2pFileUploader, FullDesignUploader, CvInlineNotification },

  props: {
    languageCode: {
      type: String as PropType<SupportedLangCodes>,
      required: false,
      default: 'en',
    },
    masterProductSku: {
      type: String,
      required: true,
    },
    sapCustomerId: {
      type: String,
      required: true,
    },
    userId: {
      type: String,
      required: false,
      default: '',
    },
    userFullName: {
      type: String,
      default: '',
    },
    context: {
      type: String as PropType<'Customer' | 'CustomerCare' | 'PrePress'>,
      required: true,
    },
    orderNumber: {
      type: String,
      required: false,
      default: '',
    },
    orderLineId: {
      type: String,
      required: false,
      default: '',
    },
    selectedPrintPositionData: {
      type: Array as PropType<SelectedPrintPositionData[]>,
      required: false,
      default() {
        return [];
      },
    },
    instructions: {
      type: String,
      default: '',
    },
    sendMailToCustomerCare: {
      type: Boolean,
      default: false,
    },
    selectedMethod: {
      type: String,
      required: true,
    },
    numberOfColorsPerPrintPosition: {
      type: Array as PropType<
        {
          positionId: string;
          numberOfColors: number;
        }[]
      >,
      default() {
        return [];
      },
    },
    customerCompanyLogoUrl: {
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      helperText: '',
      api: FileService,
      supportedExtensionsForLogoOnly,
      supportedExtensions: '',
      isFileUploaderEnabled: true,
      nextButtonEnabled: false,
      isFileUploaded: false,
      isFileValid: false,
      productPrintData: {} as ProductPrintData,
      uploadedFilesDataPerPrintPosition: [] as {
        positionSAPCode: string;
        files: ArtworkResult[];
      }[],
      preparedValidationResults: [] as PreparedValidationResult[],
      uploadOptions: {} as Artwork,
      personalizationId: '',
      uploadedArtworks: [] as ArtworkResult[],
      productIsPrintable: true,
      enableMultiLogoUpload: false,
    };
  },

  computed: {
    ...mapStores(useProductStore),
    w2pFileUploadId(): string {
      return this.selectedPrintPositionData.length === 0
        ? 'w2p-file-uploader'
        : 'w2p-file-uploader-' +
            this.selectedPrintPositionData[0].printPositionCode;
    },
  },

  async mounted() {
    const customerId = await this.populateCustomerId(this.sapCustomerId);

    this.uploadOptions = {
      externalCustomerId: this.sapCustomerId,
      customerId: customerId,
      title: '',
      shouldSaveInLogoLibrary: false,
      shouldSaveDraftForWizard: true,
    } as Artwork;

    this.nextButtonEnabled = false;
    this.$emit('update:next-button', this.nextButtonEnabled);

    // Depending on the personalise method selected, the particular text changes as well as the validations
    if (
      this.selectedMethod === 'Logo only' ||
      this.selectedMethod === 'Logo only - new flow'
    ) {
      this.helperText = FILE_UPLOADER_HELPER_TEXT_LOGO_ONLY;
      this.supportedExtensions = this.supportedExtensionsForLogoOnly;
    } else {
      this.helperText = FILE_UPLOADER_HELPER_TEXT_FULL_DESIGN;
      this.supportedExtensions = 'application/pdf';

      this.validateFn = this.customValidateFn({
        supportedExtensions: this.supportedExtensions,
      });
    }
  },

  methods: {
    ...mapActions(useProductStore, ['getProduct']),

    validateFn,
    customValidateFn,

    async populateCustomerId(sapCustomerId: string): Promise<string> {
      const { data } = await customersAPI.getAll({
        customerId: sapCustomerId,
        searchBy: {},
        page: 1,
        pageSize: 12,
      });

      if (data.page.length === 0) {
        return uuidv4();
      }

      return data.page[0].customerId;
    },
    onChange(files: Artwork[]): boolean {
      if (files.length === 0) {
        if (this.context === 'Customer') {
          this.uploadedFilesDataPerPrintPosition = [];
        }
        this.preparedValidationResults = [];
      }

      // In case the user chooses to remove the uploaded file, an event has to be emitted for disabling of the Next button in the footer
      if (files.length <= 0) {
        this.$emit('update:next-button', false);
      }

      this.isFileUploaderEnabled = files.length === 0;
      return files.length === 0;
    },
    onUploadError() {
      this.resetFlags();
    },
    resetFlags() {
      this.$emit('update:next-button', false);
      this.isFileUploaderEnabled = true;
    },

    prepareProductVariants(productVariants: ProductPrintVariant[]) {
      return productVariants.map((v) => {
        return {
          productName: v.name,
          variantColor: v.colour,
        } as NewPersonalizationVariant;
      });
    },

    async onUploadReady({
      files,
      up,
    }: {
      files: Artwork[];
      up: (files: Artwork[]) => Promise<ArtworkResult[]>;
    }) {
      if (this.context === 'Customer') {
        // ***Check if the request came from the Flow Starter (Fenego)***
        try {
          if (files.length > 0) {
            const productData = await this.getProduct(this.masterProductSku);
            console.log(
              'TheApp upload logo - onUploadReady - masterProductSku - ',
              this.masterProductSku
            );
            if (!productData.readyToPrint) {
              this.productIsPrintable = false;

              console.log('upload-logo:product-not-printable', {
                productSKU: productData.sku,
                productName: productData.name,
              } as ProductNotPrintableEvent);
              return;
            }

            this.productPrintData = productData;

            this.uploadedArtworks = [];

            this.$emit('uploaded:uploading', true);

            for (let i = 0; i < this.selectedPrintPositionData.length; i++) {
              const fileData = await up(files);

              if (fileData.length > 0) {
                this.isFileUploaded = true;
                this.uploadedFilesDataPerPrintPosition.push({
                  positionSAPCode:
                    this.selectedPrintPositionData[i].printPositionCode,
                  files: fileData,
                });

                this.uploadedArtworks.push({
                  ...fileData[0],
                  position: this.selectedPrintPositionData[i].printPositionCode,
                });
              }

              if (
                (this.selectedMethod === 'Logo only' ||
                  this.selectedMethod === 'Logo only - new flow') &&
                this.uploadedArtworks.length > 0
              ) {
                this.validateUploadedFile(
                  this.productPrintData,
                  this.uploadedFilesDataPerPrintPosition
                );
              }
              console.log('uploaded:file', {
                isFileUploaded: this.isFileUploaded,
                uploadedArtworks: this.uploadedArtworks,
                validationPipelineResults: this.preparedValidationResults,
                isFileValid: this.isFileValid,
                productPrintData: this.productPrintData,
              } as FileUploadedPayload);

              this.$emit('uploaded:file', {
                isFileUploaded: this.isFileUploaded,
                uploadedArtworks: this.uploadedArtworks,
                validationPipelineResults: this.preparedValidationResults,
                isFileValid: this.isFileValid,
                productPrintData: this.productPrintData,
                position: this.selectedPrintPositionData[i].printPositionCode,
              } as FileUploadedPayload);
            }
            this.$emit('uploaded:uploading', false);
          } else {
            this.uploadedArtworks = [];
            this.$emit('upload:reset');
          }
        } catch (error) {
          this.isFileUploaded = false;
          console.log(error);
        }
      }
    },

    async constructAddArtworkPayload(
      orderLine: OrderLineDetail,
      uploadedArtwork: ArtworkResult
    ) {
      const personalization = await customersAPI.getPersonalization(
        orderLine.personalizationId
      );
      const proof = personalization.data.proofs[0];
      const pmsColors = this.removeClosestColourIfExists(
        uploadedArtwork.pmsColorsInPngFile
      );
      const form: EnrichPositionWithArtworkForm = {
        id: uploadedArtwork.personalizationArtworkId,
        userId: this.userId,
        userFullName: this.userFullName,
        instructions: this.instructions,
        approverType: 'CustomerCare',
        pmsColorsinPngFile: pmsColors ? pmsColors : [],
        sendMailToCustomerCare: this.sendMailToCustomerCare,
      };

      return {
        personalizationId: personalization.data.id,
        proofId: proof.id,
        form,
      };
    },

    removeClosestColourIfExists(
      pmsColors: PmsColor[] | null
    ): PmsColor[] | null {
      if (pmsColors === null) {
        return null;
      }

      return pmsColors.map((c) => {
        const includes = c.name.includes('Closest Pantone:');

        if (includes) {
          c.name = c.name.split(':')[1].trimStart();
        }

        return c;
      });
    },

    // This is where the Next button will be enabled and disabled depending on the results that are returned from the validation pipeline
    validateUploadedFile(
      productPrintData: ProductPrintData,
      uploadedFilesData: { positionSAPCode: string; files: ArtworkResult[] }[]
    ) {
      const payload = {
        kind: 'Logo',
        ...uploadedFilesData[0].files[0],
        pngHorizontalResolutionDPIs:
          uploadedFilesData[0].files[0].originalHorizontalResolutionDPIs,
        pngVerticalResolutionDPIs:
          uploadedFilesData[0].files[0].originalVerticalResolutionDPIs,
      } as unknown as AnnotationShape;

      const selectedPrintPositions =
        this.prepareSelectedPrintPositions(productPrintData);

      // payload.pngHorizontalResolutionDPIs =
      //   payload.originalHorizontalResolutionDPIs;
      // payload.pngVerticalResolutionDPIs =
      //   payload.originalVerticalResolutionDPIs;

      // Constructing validation pipeline
      const pipeline = constructArtworkValidationPipeline(
        productPrintData,
        selectedPrintPositions,
        [payload],
        payload
      );

      // Executing validation pipeline
      const validationPipelineResults: ValidatorResult = pipeline({
        payload,
        validationResult: [],
      });

      // Prepare validation pipeline results for displaying
      this.preparedValidationResults = this.prepareValidationPipelineResults(
        validationPipelineResults,
        selectedPrintPositions
      );

      // Enable/disable Next button depending on the validation pipeline results
      this.isFileValid = this.checkIfFileValid();
    },

    // Map the results to the array that is going to be used for dispaying the validator results
    prepareValidationPipelineResults(
      validationPipelineResults: ValidatorResult,
      selectedPrintPositions: ProductPrintPositionBoxModel[]
    ): PreparedValidationResult[] {
      console.log('Validation results -> ', validationPipelineResults);
      const adaptedValidationPipelineResults = [] as PreparedValidationResult[];

      for (let validationResult of validationPipelineResults.validationResult) {
        const printPositionSAPCode = selectedPrintPositions.find(
          (p) => p.id === validationResult.printPositionId
        )?.name as string;

        if (
          validationResult.validatorName === 'resolutionValidator' ||
          validationResult.validatorName === 'fileTypeValidator' ||
          validationResult.validatorName === 'vectorFileValidator' ||
          validationResult.validatorName === 'gradientFileValidator'
        ) {
          let result = {} as PreparedValidationResult;

          if (validationResult.validatorColor === 'Red') {
            let validatorNotification = {} as ValidatorNotification;

            if (validationResult.validatorName === 'resolutionValidator') {
              validatorNotification = {
                validatorName: `${this.$t(
                  'upload-logo.validators.resolution.name'
                )}`,
                validatorMessage: `${printPositionSAPCode}: ${this.$t(
                  'upload-logo.validators.resolution.red'
                )}`,
              };
            } else if (validationResult.validatorName === 'fileTypeValidator') {
              validatorNotification = {
                validatorName: `${this.$t(
                  'upload-logo.validators.file-type.name'
                )}`,
                validatorMessage: `${printPositionSAPCode}: ${this.$t(
                  'upload-logo.validators.file-type.red'
                )}`,
              };
            } else if (
              validationResult.validatorName === 'gradientFileValidator'
            ) {
              validatorNotification = {
                validatorName: `${this.$t(
                  'upload-logo.validators.gradient-file.name'
                )}`,
                validatorMessage: `${printPositionSAPCode}: ${this.$t(
                  'upload-logo.validators.gradient-file.red'
                )}`,
              };
            } else {
              validatorNotification = {
                validatorName: `${this.$t(
                  'upload-logo.validators.vector-file.name'
                )}`,
                validatorMessage: `${printPositionSAPCode}: ${this.$t(
                  'upload-logo.validators.vector-file.red'
                )}`,
              };
            }

            result = {
              validatorName: validationResult.validatorName,
              validatorNotification: validatorNotification,
              validatorColor: validationResult.validatorColor,
            };

            adaptedValidationPipelineResults.push(result);
          }

          if (validationResult.validatorColor === 'Orange') {
            if (validationResult.validatorName === 'resolutionValidator') {
              const validatorNotification = {
                validatorName: `${this.$t(
                  'upload-logo.validators.resolution.name'
                )}`,
                validatorMessage: `${printPositionSAPCode}: ${this.$t(
                  'upload-logo.validators.resolution.orange'
                )}`,
              };

              result = {
                validatorName: validationResult.validatorName,
                validatorNotification: validatorNotification,
                validatorColor: validationResult.validatorColor,
              };

              adaptedValidationPipelineResults.push(result);
            }
          }
        }
      }

      return adaptedValidationPipelineResults;
    },

    prepareSelectedPrintPositions(
      productPrintData: ProductPrintData
    ): ProductPrintPositionBoxModel[] {
      return this.selectedPrintPositionData.map(
        ({ printPositionCode, printTechniqueCode }) => {
          const printPosition = productPrintData.productPrintPositions.find(
            (p) => p.name === printPositionCode
          );

          if (printPosition) {
            return {
              id: printPosition.id,
              name: printPosition.name,
              originalImageUrl: printPosition.originalImageUrl,
              originalImageDpi: printPosition.originalImageDpi,
              maxWidthMm: printPosition.maxWidthMm,
              maxHeightMm: printPosition.maxHeightMm,
              techniqueId: printPosition?.printTechniques?.find(
                (t) => t.printTechnique.code === printTechniqueCode
              )?.printTechnique.id,
              maximumColours: printPosition?.printTechniques?.find(
                (t) => t.printTechnique.code === printTechniqueCode
              )?.maximumColours,
            } as ProductPrintPositionBoxModel;
          }

          return {} as ProductPrintPositionBoxModel;
        }
      );
    },

    // Enabling/disabling Next button in footer
    checkIfFileValid(): boolean {
      let isValid = true;

      for (let result of this.preparedValidationResults) {
        if (result?.validatorColor === 'Red') {
          isValid = false;
          break;
        }
      }

      return isValid;
    },

    generateNotificationSubTitle(): string {
      let printPositionsSubTitle = '';

      this.selectedPrintPositionData.forEach((p) => {
        printPositionsSubTitle = printPositionsSubTitle.concat(
          `${p.printPositionCode}, `
        );
      });

      printPositionsSubTitle = printPositionsSubTitle.substring(
        0,
        printPositionsSubTitle.length - 2
      );

      return printPositionsSubTitle;
    },
  },
});
